import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import type { MenuActionItem } from 'src/interfaces/ui'
import { getInsurancePolicyProductDeletingId } from 'src/selectors/insurancePolicy'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  policyProduct: InsurancePolicyProduct
  onEdit(policyProduct: InsurancePolicyProduct): void
  onDelete(policyProduct: InsurancePolicyProduct): void
}

const InsurancePolicyProductActionsCellComponent = ({ policyProduct, onEdit, onDelete }: Props) => {
  const deletingProductId = useSelector(getInsurancePolicyProductDeletingId)
  const isDeleting = deletingProductId === policyProduct.insuranceProductId

  const items = useMemo<MenuActionItem[]>(() => {
    return [
      {
        label: 'Edit',
        icon: <GSIcon iconName="gs_pencil" size={20} css={tw`text-shades-1000`} />,
        disabled: isDeleting,
        dataTestId: `insurance-policy-products-list-edit-${policyProduct.insuranceProductId}`,
        onClick: () => {
          onEdit(policyProduct)
        },
      },
      {
        label: isDeleting ? 'Removing…' : 'Remove',
        icon: <GSIcon iconName="gs_trash_can" size={20} css={tw`text-critical-500`} />,
        color: 'var(--mui-critical-500)',
        disabled: isDeleting,
        dataTestId: `insurance-policy-products-list-delete-${policyProduct.insuranceProductId}`,
        onClick: () => {
          onDelete(policyProduct)
        },
      },
    ]
  }, [policyProduct, isDeleting, onEdit, onDelete])

  return (
    <RowActionsMenu
      items={items}
      ariaLabel="Policy product actions"
      dataTestId={`insurance-policy-products-list-actions-${policyProduct.insuranceProductId}`}
    />
  )
}

export const InsurancePolicyProductActionsCell = memo(InsurancePolicyProductActionsCellComponent)
